import { appServiceClient } from '@/infrastructure/http/httpClient';

import {
  AjusteEmVigorView,
  AjusteManualView,
  DesfazerPrevia,
  DesfazerResultado,
  EnrichedOp,
  IAndamentoDaOtimizacao,
  ImportOpsPayload,
  ImportResult,
  IMudancaSelecionada,
  IPlacarDaSelecao,
  IPreviaDaTrocaDeModo,
  IPreviaDoModoPayload,
  IPreviaDoTempoDoProduto,
  IPreviaDoTempoPayload,
  IProdutoDaCarteira,
  IPropostaAplicada,
  IPropostaDeProgramacao,
  ISaidasClassificadas,
  ITempoDisponivel,
  LiberacaoEstado,
  ListOpsQuery,
  ObjetivoDaOtimizacao,
  OpsSummary,
  PaginatedOpsResult,
  SaidaClassificacao,
  UltimaImportacao,
  UpdateOpPayload,
} from '../interfaces/ops';

/** O teto de uma página na API (`ListQueryDto.MAX_LIMIT`): a leitura inteira pagina por ele. */
const MAX_PAGE_SIZE = 100;

class OpsRepository {
  async list(query: ListOpsQuery = {}): Promise<PaginatedOpsResult> {
    const params = new URLSearchParams();
    if (query.page !== undefined) {
      params.set('page', String(query.page));
    }
    if (query.limit !== undefined) {
      params.set('limit', String(query.limit));
    }
    if (query.search !== undefined && query.search !== '') {
      params.set('search', query.search);
    }
    if (query.marca !== undefined && query.marca !== '') {
      params.set('marca', query.marca);
    }
    if (query.celula !== undefined && query.celula !== '') {
      params.set('celula', query.celula);
    }
    if (query.produto !== undefined && query.produto !== '') {
      params.set('produto', query.produto);
    }
    if (query.farol !== undefined) {
      params.set('farol', query.farol);
    }
    if (query.situacao !== undefined) {
      params.set('situacao', query.situacao);
    }
    const qs = params.toString();
    return appServiceClient.get<PaginatedOpsResult>(qs !== '' ? `ops?${qs}` : 'ops');
  }

  /**
   * TODAS as OPs ativas de um produto, não só a primeira página: é o que a ficha do
   * produto soma, e um total cortado em 100 daria um número que não fecha com a
   * Carteira filtrada pelo mesmo produto.
   */
  async listAllByProduto(produto: string): Promise<Array<EnrichedOp>> {
    const ops: Array<EnrichedOp> = [];
    let page = 1;
    let totalPages = 1;
    while (page <= totalPages) {
      const pagina = await this.list({ produto, page, limit: MAX_PAGE_SIZE });
      ops.push(...(pagina.data ?? []));
      totalPages = pagina.totalPages ?? 1;
      page += 1;
    }
    return ops;
  }

  async recalcular(): Promise<OpsSummary> {
    return appServiceClient.post<OpsSummary>('ops/recalcular', {});
  }

  /** O que a carteira usa em cada produto dela: base da aba Produtos, em Cadastros. */
  async produtosDaCarteira(): Promise<Array<IProdutoDaCarteira>> {
    return appServiceClient.get<Array<IProdutoDaCarteira>>('ops/produtos');
  }

  /** O que gravar este tempo faria com a carteira. NÃO grava nada. */
  async previaDoTempoDoProduto(payload: IPreviaDoTempoPayload): Promise<IPreviaDoTempoDoProduto> {
    return appServiceClient.post<IPreviaDoTempoDoProduto>('ops/produtos/previa-tempo', {
      body: payload,
    });
  }

  /** O que trocar o modo de programação faria com a carteira. NÃO grava nada. */
  async previaDaTrocaDeModo(payload: IPreviaDoModoPayload): Promise<IPreviaDaTrocaDeModo> {
    return appServiceClient.post<IPreviaDaTrocaDeModo>('ops/previa-modo-programacao', {
      body: payload,
    });
  }

  /**
   * A melhor programação que o sistema encontrou. NÃO grava nada.
   *
   * O `token` é o crachá desta busca: é por ele que a tela pergunta o andamento e manda
   * cancelar enquanto o cálculo roda no servidor.
   */
  async otimizar(token: string, objetivo?: ObjetivoDaOtimizacao): Promise<IPropostaDeProgramacao> {
    return appServiceClient.post<IPropostaDeProgramacao>('ops/otimizar', {
      body: objetivo === undefined ? { token } : { token, objetivo },
    });
  }

  /** Como vai a busca deste crachá: o que a barra de progresso mostra. */
  async andamentoDaOtimizacao(token: string): Promise<IAndamentoDaOtimizacao> {
    return appServiceClient.get<IAndamentoDaOtimizacao>(`ops/otimizar/andamento/${token}`);
  }

  /** Para a busca onde ela estiver. Nada foi gravado, então não há o que desfazer. */
  async cancelarOtimizacao(token: string): Promise<IAndamentoDaOtimizacao> {
    return appServiceClient.post<IAndamentoDaOtimizacao>(`ops/otimizar/${token}/cancelar`, {
      body: {},
    });
  }

  /** O placar do que continua marcado na proposta. NÃO grava nada. */
  async previaDaSelecao(
    assinatura: string,
    mudancas: Array<IMudancaSelecionada>,
  ): Promise<IPlacarDaSelecao> {
    return appServiceClient.post<IPlacarDaSelecao>('ops/otimizar/selecao', {
      body: { assinatura, mudancas },
    });
  }

  /** Aceita as mudanças marcadas: cada OP vira movimento de gente, validada na posição. */
  async aplicarProposta(
    assinatura: string,
    mudancas: Array<IMudancaSelecionada>,
  ): Promise<IPropostaAplicada> {
    return appServiceClient.post<IPropostaAplicada>('ops/otimizar/aplicar', {
      body: { assinatura, mudancas },
    });
  }

  /** Desfaz a proposta inteira: todas as OPs voltam ao estado de antes. */
  async desfazerProposta(id: string): Promise<IPropostaAplicada> {
    return appServiceClient.post<IPropostaAplicada>(`ops/otimizar/${id}/desfazer`, { body: {} });
  }

  async findByUid(uid: string): Promise<EnrichedOp> {
    return appServiceClient.get<EnrichedOp>(`ops/${uid}`);
  }

  /**
   * Os tempos que existem para esta OP, com o valor de cada um: é a lista de "Usar
   * outro tempo nesta OP". O ritmo real não vem aqui, ele é do apontamento.
   */
  async temposDisponiveis(uid: string): Promise<Array<ITempoDisponivel>> {
    return appServiceClient.get<Array<ITempoDisponivel>>(`ops/${uid}/tempos-disponiveis`);
  }

  async update(uid: string, payload: UpdateOpPayload): Promise<EnrichedOp> {
    return appServiceClient.patch<EnrichedOp>(`ops/${uid}`, { body: payload });
  }

  /** Marks ONE release checklist item; who and when come from the session. */
  async marcarLiberacao(uid: string, chave: string, estado: LiberacaoEstado): Promise<EnrichedOp> {
    return appServiceClient.patch<EnrichedOp>(`ops/${uid}/liberacao/${chave}`, {
      body: { estado },
    });
  }

  async importar(payload: ImportOpsPayload): Promise<ImportResult> {
    return appServiceClient.post<ImportResult>('ops/importar', { body: payload });
  }

  /** O recibo da última importação que gravou; `null` quando nenhuma gravou ainda. */
  async ultimaImportacao(): Promise<UltimaImportacao | null> {
    return appServiceClient.get<UltimaImportacao | null>('ops/importacoes/ultima');
  }

  /** A última e as anteriores, da mais recente para a mais antiga. */
  async importacoes(): Promise<Array<UltimaImportacao>> {
    return appServiceClient.get<Array<UltimaImportacao>>('ops/importacoes');
  }

  /** O que o desfazer faria: os três números e o que mudou depois da importação. */
  async previaDoDesfazer(): Promise<DesfazerPrevia> {
    return appServiceClient.get<DesfazerPrevia>('ops/importacoes/desfazer/previa');
  }

  /** Desfaz a importação informada, que precisa ser a última. */
  async desfazerImportacao(id: string): Promise<DesfazerResultado> {
    return appServiceClient.post<DesfazerResultado>(`ops/importacoes/${id}/desfazer`, {});
  }

  /** Tira a OP de Excluídos: ela volta com os mesmos dados que a importação gravou. */
  async restaurar(uid: string): Promise<EnrichedOp> {
    return appServiceClient.post<EnrichedOp>(`ops/${uid}/restaurar`, {});
  }

  /**
   * Tira a OP da carteira por decisão de alguém, com o motivo escrito e o que a saída
   * foi. A classificação é obrigatória no DTO: sem ela a API responde 400.
   */
  async tirarDaCarteira(uid: string, motivo: string, classificacao: SaidaClassificacao): Promise<EnrichedOp> {
    return appServiceClient.post<EnrichedOp>(`ops/${uid}/tirar-da-carteira`, {
      body: { motivo, classificacao },
    });
  }

  /** Clears the exit and records who undid it and when. */
  async voltarParaCarteira(uid: string): Promise<EnrichedOp> {
    return appServiceClient.post<EnrichedOp>(`ops/${uid}/voltar-para-carteira`, {});
  }

  /**
   * Corrige o TIPO de uma saída que já aconteceu. A data e o motivo escrito ficam como
   * estão: o que muda é só o que aquela saída foi, e isso decide se a OP entra no
   * "prometido contra entregue".
   */
  async reclassificarSaida(uid: string, classificacao: SaidaClassificacao): Promise<EnrichedOp> {
    return appServiceClient.patch<EnrichedOp>(`ops/${uid}/saida-classificacao`, {
      body: { classificacao },
    });
  }

  /**
   * O mesmo tipo na seleção inteira da aba "Saíram". As 27 OPs que sumiram juntas de
   * uma importação são uma decisão só, e a API responde quantas mudaram de fato.
   */
  async reclassificarSaidasEmLote(
    uids: ReadonlyArray<string>,
    classificacao: SaidaClassificacao,
  ): Promise<ISaidasClassificadas> {
    return appServiceClient.patch<ISaidasClassificadas>('ops/saida-classificacao', {
      body: { uids: [...uids], classificacao },
    });
  }

  /**
   * The manual adjustments IN FORCE, one row per order, with the undo already resolved.
   * The single list: the Map panel and the portfolio's "with manual adjustment" slice
   * read this same response.
   */
  async ajustesEmVigor(): Promise<Array<AjusteEmVigorView>> {
    return appServiceClient.get<Array<AjusteEmVigorView>>('ajustes-manuais/em-vigor');
  }

  async desfazerAjuste(id: string): Promise<AjusteManualView> {
    return appServiceClient.post<AjusteManualView>(`ajustes-manuais/${id}/desfazer`, {});
  }
}

export const opsRepository = new OpsRepository();
